'use strict';
/*
 * Helpers for the licence screens (Hepi release 26.10). Pure functions: no Vue, no DOM, no network. Whatever they need
 * (the storage, the route, the licence) comes in as a parameter, so `npm test` (node --test test/) runs them as they are.
 *
 * This file is written as CommonJS on purpose: node loads it without a bundler, and webpack imports it by name like any other
 * module (`import { formatPaise } from '@/module/licence.js'`).
 *
 * The API it serves is docs/release-26.10/licence-api-contract.md (kastio repository), sections 0, 1, 3, 5 and 6.
 * Money is an integer number of paise everywhere; screens show rupees with Indian grouping.
 */

const LICENCE_KEY = 'licence';
// The pages the owner has switched off for everyone (the `switched_off` list of the role data; absent until the owner's marks are applied).
const SWITCHED_OFF_KEY = 'switched_off';
const NOT_IN_PLAN_ROUTE = 'NotInPlan';
// The guard never redirects these: the target itself, sign-in and sign-out, and the Dashboard (it belongs to `core`, which a
// licence cannot take away).
const NEVER_REDIRECTED = [NOT_IN_PLAN_ROUTE, 'Login', 'Logout', 'Dashboard'];

const STATUSES = ['pending_payment', 'active', 'grace', 'suspended', 'cancelled'];
const SOURCES = ['sale', 'trial', 'legacy_hepi'];
const REASON_MAX = 500;
const NOTE_MAX = 1000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ------------------------------------------------------------------------------------------------
// Small things
// ------------------------------------------------------------------------------------------------

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isBlank(value) {
    return value === null || value === undefined || value === '';
}

/** "pending_payment" -> "Pending payment". */
function humanize(text) {
    const s = String(isBlank(text) ? '' : text).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Text that goes into a toast is shown as HTML by the toaster, so anything that came from a server is escaped first. */
function escapeHtml(text) {
    return String(isBlank(text) ? '' : text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
}

/** Deep equality for JSON values; the order of object keys does not matter. */
function deepEqual(a, b) {
    if (a === b) return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
    }
    if (isPlainObject(a) && isPlainObject(b)) {
        const ka = Object.keys(a);
        const kb = Object.keys(b);
        return ka.length === kb.length && ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
    }
    return false;
}

// ------------------------------------------------------------------------------------------------
// The licence the portal keeps in the browser (localStorage key `licence`)
// ------------------------------------------------------------------------------------------------

/** The browser's localStorage, or null where there is none (a script run by node) or where it is blocked (reading it can throw). */
function browserStorage() {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    } catch (e) {
        return null;
    }
}

/** The cached licence object (the `licence` block of the role data), or null when there is none or it cannot be read. */
function readLicence(storage) {
    try {
        if (!storage || typeof storage.getItem !== 'function') return null;
        const raw = storage.getItem(LICENCE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isPlainObject(parsed) ? parsed : null;
    } catch (e) {
        return null;
    }
}

/**
 * Keep the licence block of the role data, or REMOVE the key when the answer has none, so that an organisation without a
 * licence (or a legacy one) behaves exactly as before. Returns true when a licence was stored.
 */
function storeLicence(storage, licence) {
    try {
        if (!storage) return false;
        if (isPlainObject(licence)) {
            storage.setItem(LICENCE_KEY, JSON.stringify(licence));
            return true;
        }
        storage.removeItem(LICENCE_KEY);
    } catch (e) {
        // storage can be blocked (a private window): the guard then simply has nothing to go on
    }
    return false;
}

/** True when the licence is enforced and the page (the route name is the module name) is not in the plan. */
function isNotInPlan(routeName, licence) {
    if (isBlank(routeName) || !isPlainObject(licence) || !licence.enforced) return false;
    return Array.isArray(licence.not_in_plan) && licence.not_in_plan.includes(routeName);
}

/** The pages switched off for everyone (the thin portal), as cached from the role data; [] when there are none or the list cannot be read. */
function readSwitchedOff(storage) {
    try {
        if (!storage || typeof storage.getItem !== 'function') return [];
        const raw = storage.getItem(SWITCHED_OFF_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((name) => typeof name === 'string' && name) : [];
    } catch (e) {
        return [];
    }
}

/** Keep the list of switched-off pages, or REMOVE the key when the answer has none (the usual case). Returns true when a list was stored. */
function storeSwitchedOff(storage, list) {
    try {
        if (!storage) return false;
        if (Array.isArray(list) && list.length) {
            storage.setItem(SWITCHED_OFF_KEY, JSON.stringify(list));
            return true;
        }
        storage.removeItem(SWITCHED_OFF_KEY);
    } catch (e) {
        // blocked storage: the guard then has nothing to go on and the page simply opens
    }
    return false;
}

/** True when the page (the route name is the module name) is in the list of pages the owner switched off. */
function isSwitchedOff(routeName, list) {
    return !isBlank(routeName) && Array.isArray(list) && list.includes(routeName);
}

/**
 * What the router guard returns for the route `to`: true to go on, or the location of the page that says why it is not available:
 * "Not in your plan" for a page outside an enforced licence, or the same page with `reason=switched_off` for a page the owner switched
 * off. Both lists come from the role data; with neither, every route goes through exactly as before.
 */
function guardTarget(to, licence, switchedOff) {
    if (!to || isBlank(to.name) || NEVER_REDIRECTED.includes(to.name)) return true;
    if (isNotInPlan(to.name, licence)) return { name: NOT_IN_PLAN_ROUTE, query: { page: String(to.name) }, replace: true };
    if (isSwitchedOff(to.name, switchedOff)) return { name: NOT_IN_PLAN_ROUTE, query: { page: String(to.name), reason: 'switched_off' }, replace: true };
    return true;
}

// ------------------------------------------------------------------------------------------------
// Errors from the API
// ------------------------------------------------------------------------------------------------

/** The licence and Companion mounts answer { error: { code, message, details?, request_id } }. Anything else gives null. */
function licenceError(body) {
    if (!isPlainObject(body) || !isPlainObject(body.error)) return null;
    const e = body.error;
    if (typeof e.code !== 'string' || !e.code) return null;
    return {
        code: e.code,
        message: typeof e.message === 'string' && e.message ? e.message : null,
        details: isPlainObject(e.details) ? e.details : null,
        request_id: typeof e.request_id === 'string' ? e.request_id : null
    };
}

/**
 * What the response interceptor does with the errors of the new JSON style. Returns { message } when the error is to be shown
 * as a toast and the user is NOT signed out, or null when the old behaviour applies (every 401, the plain-text 403, other 404s).
 */
function licenceErrorOutcome(status, body) {
    const e = licenceError(body);
    if (!e) return null;
    if (status === 403 && e.code === 'LICENCE_NOT_INCLUDED') return { message: 'This feature is not in your plan.' };
    if (status === 404 && e.code === 'FEATURE_SWITCHED_OFF') return { message: 'This feature is switched off.' };
    if (status === 403) return { message: e.message || 'You are not allowed to do this.' };
    return null;
}

const STATUS_MESSAGES = {
    400: 'The request is not valid.',
    401: 'Your session has ended. Sign in again.',
    403: 'You are not allowed to do this.',
    404: 'This was not found, or you may not see it.',
    409: 'This conflicts with the current state.',
    413: 'The file or request is too large.',
    422: 'This request breaks a rule.',
    429: 'Too many attempts. Try again in a minute.'
};

/** An error in one shape for the screens: { status, code, message, fields, details, requestId, handled }. */
function normaliseError(status, body, handled) {
    const e = licenceError(body);
    const code = e ? e.code : null;
    let message = e && e.message ? e.message : null;
    if (!message && typeof body === 'string' && body.trim() && body.length <= 200 && !/[<>]/.test(body)) message = body.trim();
    if (!message) {
        if (!status) message = 'Could not reach the server. Check your connection and try again.';
        else if (status >= 500) message = 'Something went wrong on the server. Please try again.';
        else message = STATUS_MESSAGES[status] || 'The request could not be completed.';
    }
    const fields = e && e.details && isPlainObject(e.details.fields) ? e.details.fields : null;
    return { status: status || 0, code, message, fields, details: e ? e.details : null, requestId: e ? e.request_id : null, handled: !!handled };
}

/** The messages of a field-error map as lines: ["gstin: Not a valid GSTIN."]. */
function fieldErrorLines(fields) {
    if (!isPlainObject(fields)) return [];
    return Object.keys(fields).map((k) => `${humanize(k)}: ${fields[k]}`);
}

// ------------------------------------------------------------------------------------------------
// Money (integer paise) and dates
// ------------------------------------------------------------------------------------------------

/** 1500000 -> "15,00,000" (Indian grouping: the last three digits, then pairs). */
function groupIndian(digits) {
    if (digits.length <= 3) return digits;
    const head = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return `${head},${digits.slice(-3)}`;
}

/** 150000000 paise -> "₹15,00,000"; 1500050 -> "₹15,000.50"; null or anything that is not a number -> "—". */
function formatPaise(value) {
    if (isBlank(value) || typeof value === 'boolean') return '—';
    const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
    const paise = Math.round(Math.abs(n));
    if (!Number.isSafeInteger(paise)) return '—';
    const rupees = Math.floor(paise / 100);
    const rest = paise % 100;
    const text = `₹${groupIndian(String(rupees))}${rest ? `.${String(rest).padStart(2, '0')}` : ''}`;
    return n < 0 && paise !== 0 ? `-${text}` : text;
}

/**
 * Rupees typed by a person -> whole paise, or null when it is not a clean amount. "15,000.50" -> 1500050, "₹15,00,000" -> 150000000,
 * "10.555" -> null, "abc" -> null, "-5" -> null. Zero is a valid answer (the caller decides whether zero is allowed).
 * No floating point is used: the digits are cut apart as text.
 */
function rupeesToPaise(text) {
    let s = text;
    if (typeof s === 'number') {
        if (!Number.isFinite(s)) return null;
        s = String(s);
    }
    if (typeof s !== 'string') return null;
    s = s.trim().replace(/^₹\s*/, '');
    const plain = /^\d+(\.\d{1,2})?$/;
    const international = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;
    const indian = /^\d{1,2}(,\d{2})*,\d{3}(\.\d{1,2})?$/;
    if (!plain.test(s) && !international.test(s) && !indian.test(s)) return null;
    const [whole, frac = ''] = s.replace(/,/g, '').split('.');
    if (whole.replace(/^0+/, '').length > 15) return null;
    const paise = Number(whole) * 100 + Number(`${frac}00`.slice(0, 2));
    return Number.isSafeInteger(paise) ? paise : null;
}

/** Paise -> the text that goes back into a rupee input: 1500050 -> "15000.50", 1500000 -> "15000", null -> "". */
function paiseToRupeesInput(value) {
    if (isBlank(value)) return '';
    const n = Number(value);
    if (!Number.isSafeInteger(n) || n < 0) return '';
    const rest = n % 100;
    return `${Math.floor(n / 100)}${rest ? `.${String(rest).padStart(2, '0')}` : ''}`;
}

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseWhen(value) {
    if (isBlank(value)) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function zonedParts(date, timeZone) {
    let format;
    try {
        format = new Intl.DateTimeFormat('en-US', { timeZone: timeZone || undefined, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
    } catch (e) {
        format = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
    }
    const out = {};
    for (const part of format.formatToParts(date)) out[part.type] = part.value;
    return { year: Number(out.year), month: Number(out.month), day: Number(out.day), hour: Number(out.hour) % 24, minute: Number(out.minute) };
}

/**
 * "26 Oct 2026". An ISO time is shown in `timeZone` (default: the time zone of the browser); a plain YYYY-MM-DD is a calendar
 * date and is never shifted. Nothing usable gives "—".
 */
function formatDate(value, timeZone) {
    const d = parseWhen(value);
    if (!d) return '—';
    const p = zonedParts(d, typeof value === 'string' && PLAIN_DATE.test(value) ? 'UTC' : timeZone);
    return `${p.day} ${MONTHS[p.month - 1]} ${p.year}`;
}

/** "26 Oct 2026, 7:03 pm". */
function formatDateTime(value, timeZone) {
    const d = parseWhen(value);
    if (!d) return '—';
    if (typeof value === 'string' && PLAIN_DATE.test(value)) return formatDate(value);
    const p = zonedParts(d, timeZone);
    const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
    return `${p.day} ${MONTHS[p.month - 1]} ${p.year}, ${hour12}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'}`;
}

/** The value of a date input ("2027-10-26") for an ISO time, in `timeZone` (default: the browser's); '' when there is none. */
function toDateInput(value, timeZone) {
    const d = parseWhen(value);
    if (!d) return '';
    const p = zonedParts(d, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** How far `timeZone` (default: the browser's) is ahead of UTC at this instant, in milliseconds. */
function zoneOffsetMs(instant, timeZone) {
    const p = zonedParts(instant, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(instant.getTime() / 60000) * 60000;
}

/**
 * The end of a calendar day as an ISO time in UTC: a licence runs to the end of the day the person picked, in `timeZone` (default:
 * the time zone of the browser). "2027-10-26" in Asia/Kolkata is "2027-10-26T18:29:59.000Z". Not a real date gives null.
 */
function endOfDayIso(dateText, timeZone) {
    const m = typeof dateText === 'string' ? PLAIN_DATE.exec(dateText.trim()) : null;
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const check = new Date(Date.UTC(y, mo - 1, d));
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
    const wall = Date.UTC(y, mo - 1, d, 23, 59, 59);
    const first = wall - zoneOffsetMs(new Date(wall), timeZone);
    return new Date(wall - zoneOffsetMs(new Date(first), timeZone)).toISOString();
}

/** 182233 -> "178 KB". */
function fileSizeLabel(bytes) {
    const n = Number(bytes);
    if (isBlank(bytes) || !Number.isFinite(n) || n < 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ------------------------------------------------------------------------------------------------
// Words for the codes the API sends
// ------------------------------------------------------------------------------------------------

const STATUS_LABELS = { pending_payment: 'Pending payment', active: 'Active', grace: 'Grace period', suspended: 'Suspended', cancelled: 'Cancelled', none: 'No licence' };
const STATUS_TONES = { pending_payment: 'info', active: 'ok', grace: 'warn', suspended: 'bad', cancelled: 'muted', none: 'muted' };

/** "No licence" for null, "Pending payment", "Active", "Grace period", "Suspended", "Cancelled". */
function statusLabel(status) {
    if (isBlank(status)) return STATUS_LABELS.none;
    return STATUS_LABELS[status] || humanize(status);
}

/** The colour family of a status badge: ok, info, warn, bad or muted. */
function statusTone(status) {
    if (isBlank(status)) return STATUS_TONES.none;
    return STATUS_TONES[status] || 'muted';
}

const CAPABILITY_TITLES = {
    core: 'Home, apps and settings',
    tv_input: 'TV input',
    iptv: 'Live TV (Hepi IPTV)',
    offers_watch: 'Offers and Watch',
    food_services: 'Food and Services'
};

function capabilityTitle(code) {
    if (isBlank(code)) return '—';
    return CAPABILITY_TITLES[code] || humanize(code);
}

const PLAN_FALLBACK_NAMES = {
    BASE_IPTV: 'Hepi Base with IPTV',
    BASE_INPUT: 'Hepi Base with TV Input',
    PREM_IPTV: 'Hepi Premium with IPTV',
    PREM_INPUT: 'Hepi Premium with TV Input',
    PLUS_IPTV: 'Hepi Premium Plus with IPTV',
    PLUS_INPUT: 'Hepi Premium Plus with TV Input',
    LEGACY_HEPI: 'Hepi (existing property)'
};

/** The name of a plan: from the plan list when it is there, else the known name of the code, else the code made readable. */
function planName(code, plans) {
    if (isBlank(code)) return '—';
    const found = Array.isArray(plans) ? plans.find((p) => p && p.plan_code === code) : null;
    if (found && found.name) return found.name;
    return PLAN_FALLBACK_NAMES[code] || humanize(String(code).toLowerCase());
}

/** The label of the plan the dry-run proposes: its name, or "No proposal" when it proposes none. */
function proposeLabel(planCode, plans) {
    return isBlank(planCode) ? 'No proposal' : planName(planCode, plans);
}

function confidenceLabel(confidence) {
    if (confidence === 'exact') return 'Exact';
    if (confidence === 'approximate') return 'Approximate';
    return isBlank(confidence) ? '—' : humanize(confidence);
}

const SOURCE_LABELS = { sale: 'Sale', trial: 'Trial', legacy_hepi: 'Existing property' };

function sourceLabel(source) {
    if (isBlank(source)) return '—';
    return SOURCE_LABELS[source] || humanize(source);
}

function enforcementLabel(enforced) {
    return enforced ? 'Enforced' : 'Observing';
}

const BLOCKER_LABELS = {
    kyc_not_submitted: 'KYC not submitted yet',
    kyc_not_approved: 'KYC not approved yet',
    licence_not_assigned: 'No licence assigned',
    payment_not_confirmed: 'Payment not confirmed'
};

function blockerLabel(code) {
    return BLOCKER_LABELS[code] || humanize(code);
}

/** The reasons a licence is not active yet as one line: "KYC not approved yet; Payment not confirmed". '' when there are none. */
function blockersText(codes) {
    return Array.isArray(codes) ? codes.map(blockerLabel).join('; ') : '';
}

const KYC_LABELS = { draft: 'Draft', submitted: 'Submitted, waiting for review', approved: 'Approved', rejected: 'Rejected', changes_requested: 'Changes requested' };
const KYC_TONES = { draft: 'muted', submitted: 'info', approved: 'ok', rejected: 'bad', changes_requested: 'warn' };

function kycStatusLabel(status) {
    if (isBlank(status)) return 'No KYC yet';
    return KYC_LABELS[status] || humanize(status);
}

function kycStatusTone(status) {
    return KYC_TONES[status] || 'muted';
}

const PAYMENT_LABELS = { initiated: 'Waiting for the payer', reported: 'Reported, waiting for the bank statement', confirmed: 'Confirmed', failed: 'Rejected or failed', cancelled: 'Cancelled', expired: 'Expired' };
const PAYMENT_TONES = { initiated: 'info', reported: 'warn', confirmed: 'ok', failed: 'bad', cancelled: 'muted', expired: 'muted' };
const METHOD_LABELS = { online_link: 'Online payment', offline_reference: 'Bank transfer', owner_approval: 'Approved without payment' };

function paymentStatusLabel(status) {
    if (isBlank(status)) return '—';
    return PAYMENT_LABELS[status] || humanize(status);
}

function paymentStatusTone(status) {
    return PAYMENT_TONES[status] || 'muted';
}

function paymentMethodLabel(method) {
    if (isBlank(method)) return '—';
    return METHOD_LABELS[method] || humanize(method);
}

const DOC_LABELS = { pan_card: 'PAN card', gst_certificate: 'GST certificate', signatory_id: 'Signatory ID', address_proof: 'Address proof', hotel_photo: 'Hotel photo', cancelled_cheque: 'Cancelled cheque' };

function docTypeLabel(type) {
    if (isBlank(type)) return '—';
    return DOC_LABELS[type] || humanize(type);
}

const CHECK_LABELS = { unchecked: 'Not checked', verified: 'Verified', failed: 'Failed', error: 'Provider error' };
const CHECK_TONES = { unchecked: 'muted', verified: 'ok', failed: 'bad', error: 'warn' };
const CHECK_MODES = { surepass: 'Surepass', sandbox: 'Sandbox (test only)', manual: 'Manual review only', none: 'Manual review only' };

function checkLabel(status) {
    if (isBlank(status)) return CHECK_LABELS.unchecked;
    return CHECK_LABELS[status] || humanize(status);
}

function checkTone(status) {
    return CHECK_TONES[status] || 'muted';
}

function checkModeLabel(mode) {
    if (isBlank(mode)) return '—';
    return CHECK_MODES[mode] || humanize(mode);
}

const KIND_LABELS = { api: 'Portal API', entity: 'Launcher entity', request: 'TV request' };

function observationKindLabel(kind) {
    if (isBlank(kind)) return '—';
    return KIND_LABELS[kind] || humanize(kind);
}

/** "PRIMARY" -> "Primary". */
function orgTypeLabel(type) {
    if (isBlank(type)) return '—';
    return humanize(String(type).toLowerCase());
}

const EVENT_LABELS = {
    assigned: 'Licence assigned',
    assigned_by_staff: 'Licence assigned by staff',
    plan_changed: 'Plan changed',
    reassigned: 'Licence assigned again after it was cancelled',
    changed: 'Licence details changed',
    status_changed: 'Status changed',
    status_active: 'Licence reactivated',
    status_suspended: 'Licence suspended',
    status_cancelled: 'Licence cancelled',
    activated: 'Licence activated',
    approved_without_payment: 'Activation approved without payment',
    enforcement_changed: 'Enforcement changed',
    payment_started: 'Online payment started',
    payment_reported: 'Bank transfer reported',
    payment_confirmed: 'Payment confirmed',
    payment_rejected: 'Payment rejected',
    payment_cancelled: 'Payment cancelled',
    kyc_submitted: 'KYC submitted',
    kyc_approved: 'KYC approved',
    kyc_rejected: 'KYC rejected',
    kyc_changes_requested: 'KYC changes requested',
    hotel_created: 'Hotel created',
    hotel_updated: 'Hotel details updated',
    backfill: 'Existing property: legacy licence given, nothing changes',
    inherited: 'Licence started from the organisation that created this one'
};

/** Human English for an event type of the licence history. An unknown type is made readable instead of being hidden. */
function eventLabel(type) {
    if (isBlank(type)) return '—';
    return EVENT_LABELS[type] || humanize(type);
}

/** Who did it, for one event: "Test INSTALLER (INSTALLER)", "System", "Payment provider (razorpay)". */
function actorLabel(event) {
    const e = event || {};
    if (e.actor_kind === 'system') return e.actor_name ? String(e.actor_name) : 'System';
    if (e.actor_kind === 'provider') return e.actor_name ? `Payment provider (${e.actor_name})` : 'Payment provider';
    const name = e.actor_name ? String(e.actor_name) : 'Unknown user';
    return e.actor_role && e.actor_role !== name ? `${name} (${e.actor_role})` : name;
}

// ------------------------------------------------------------------------------------------------
// What an event changed (before -> after)
// ------------------------------------------------------------------------------------------------

function asObject(value) {
    if (isPlainObject(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return isPlainObject(parsed) ? parsed : null;
        } catch (e) {
            return null;
        }
    }
    return null;
}

function overridesText(value) {
    const o = asObject(value) || {};
    const add = Array.isArray(o.add) ? o.add : [];
    const remove = Array.isArray(o.remove) ? o.remove : [];
    if (!add.length && !remove.length) return 'None';
    return [...add.map((c) => `+ ${capabilityTitle(c)}`), ...remove.map((c) => `− ${capabilityTitle(c)}`)].join(', ');
}

/** The fields a licence event keeps, in the order they are shown: [key, label, how to show the value, empty on first assignment]. */
const LICENCE_FIELDS = [
    ['plan_code', 'Plan', (v, plans) => planName(v, plans), (v) => isBlank(v)],
    ['status', 'Status', (v) => statusLabel(v), (v) => isBlank(v)],
    ['source', 'Source', (v) => sourceLabel(v), (v) => isBlank(v)],
    ['enforce', 'Enforcement', (v) => enforcementLabel(v), () => false],
    ['amount_agreed_paise', 'Agreed amount', (v) => formatPaise(v), (v) => isBlank(v)],
    ['valid_from', 'Valid from', (v) => formatDate(v), (v) => isBlank(v)],
    ['valid_to', 'Valid to', (v) => formatDate(v), (v) => isBlank(v)],
    ['capability_overrides', 'Capability changes', (v) => overridesText(v), (v) => overridesText(v) === 'None'],
    ['approval_activation', 'Activated by approval', (v) => (v ? 'Yes' : 'No'), (v) => !v]
];

function sameValue(a, b) {
    if (isBlank(a) && isBlank(b)) return true;
    return deepEqual(a, b);
}

/**
 * The lines "label: from -> to" an event shows. [{ label, from, to }]; `from` is null when the value was set for the first time.
 * `plans` (optional) turns plan codes into names. Events that change nothing a person needs to read give [].
 */
function describeChange(event, plans) {
    const type = event && event.event_type;
    const before = asObject(event && event.before);
    const after = asObject(event && event.after);
    if (!after && !before) return [];

    if (typeof type === 'string' && type.startsWith('kyc_')) {
        if (!after || isBlank(after.status)) return [];
        return [{ label: 'KYC status', from: before && !isBlank(before.status) ? kycStatusLabel(before.status) : null, to: kycStatusLabel(after.status) }];
    }
    if (typeof type === 'string' && type.startsWith('payment_')) {
        const lines = [];
        if (after && !isBlank(after.amount_paise)) lines.push({ label: 'Amount', from: null, to: formatPaise(after.amount_paise) });
        if (after && after.method) lines.push({ label: 'Method', from: null, to: paymentMethodLabel(after.method) });
        if (after && after.bank_reference) lines.push({ label: 'Bank reference', from: null, to: String(after.bank_reference) });
        return lines;
    }
    if (type === 'hotel_created') return after && after.org_name ? [{ label: 'Name', from: null, to: String(after.org_name) }] : [];
    if (type === 'hotel_updated') {
        const fields = after && Array.isArray(after.fields) ? after.fields : [];
        return fields.length ? [{ label: 'Fields changed', from: null, to: fields.map((f) => humanize(f)).join(', ') }] : [];
    }
    if (type === 'backfill') return [];

    const lines = [];
    for (const [key, label, show, emptyAtStart] of LICENCE_FIELDS) {
        const was = before ? before[key] : undefined;
        const now = after ? after[key] : undefined;
        if (!before) {
            if (after && key in after && !emptyAtStart(now)) lines.push({ label, from: null, to: show(now, plans) });
        } else if (after && key in after && !sameValue(was, now)) {
            lines.push({ label, from: isBlank(was) ? '—' : show(was, plans), to: isBlank(now) ? '—' : show(now, plans) });
        }
    }
    return lines;
}

// ------------------------------------------------------------------------------------------------
// Who may do what, and what can be done to a licence
// ------------------------------------------------------------------------------------------------

/**
 * The buttons a staff member gets. `permissions` is the `permissions` object of GET /api/companion/me; `can_approve_kyc` is true
 * only for the ADMIN role, so it is the signal for "ADMIN staff". `privileges` is the cached privilege map (licence_grid, licence_add
 * for POST, licence_edit for PUT): the server needs both, so a button needs both. Everything is false until the answer is known.
 */
function staffFlags(permissions, privileges) {
    const p = isPlainObject(permissions) ? permissions : {};
    const priv = isPlainObject(privileges) ? privileges : {};
    const admin = !!p.can_approve_kyc;
    return {
        canView: !!priv.licence_grid,
        isAdminStaff: admin,
        canChange: admin && !!priv.licence_edit,
        canAct: admin && !!priv.licence_add,
        canReviewKyc: admin && !!priv.licence_add,
        canConfirmPayment: !!p.can_confirm_payment && !!priv.licence_add
    };
}

/** What can be done to a licence now. The server judges by the STORED status (an expired licence is still stored as active). */
function availableActions(licence) {
    if (!isPlainObject(licence)) return { assign: true, activate: false, suspend: false, reactivate: false, cancel: false, enforce: false };
    const s = licence.status;
    return {
        assign: true,
        activate: s === 'pending_payment',
        suspend: s === 'active' || s === 'grace',
        reactivate: s === 'suspended' || s === 'cancelled',
        cancel: ['pending_payment', 'active', 'grace', 'suspended'].includes(s),
        enforce: true
    };
}

/** A person's reason or note: trimmed, required or not, with a length limit. { ok, value, error }. */
function cleanReason(text, { required = true, max = REASON_MAX, what = 'a reason' } = {}) {
    const value = typeof text === 'string' ? text.trim() : '';
    if (!value && required) return { ok: false, value, error: `Please give ${what}.` };
    if (value.length > max) return { ok: false, value, error: `Please keep it to ${max} characters or fewer.` };
    return { ok: true, value, error: null };
}

/** The source to propose when the plan changes: an existing property moved to a real plan is a sale, not a legacy licence. */
function suggestSource(currentSource, planCode) {
    if (planCode === 'LEGACY_HEPI') return 'legacy_hepi';
    if (currentSource === 'legacy_hepi' || isBlank(currentSource)) return 'sale';
    return currentSource;
}

/** The plans a person may choose: those on sale and active, plus the one the licence has now (even if it is not sold any more). */
function planChoices(plans, currentCode) {
    const list = Array.isArray(plans) ? plans : [];
    return list.filter((p) => p && ((p.sellable && p.active) || p.plan_code === currentCode));
}

// ------------------------------------------------------------------------------------------------
// Forms -> request bodies
// ------------------------------------------------------------------------------------------------

/**
 * The body of PUT /api/licence/orgs/:org_id from the change form. `current` is the licence now (null: a first assignment); only
 * what differs from it is sent, with the reason. `draft`: { planCode, status, source, amountText, validTo ("YYYY-MM-DD" or ''), reason }.
 * Returns { ok, errors: { field: text }, body }.
 */
function licenceChangeBody(current, draft, timeZone) {
    const d = draft || {};
    const errors = {};
    const body = {};

    if (isBlank(d.planCode)) errors.plan_code = 'Choose a plan.';
    if (!STATUSES.includes(d.status)) errors.status = 'Choose a status.';
    if (!SOURCES.includes(d.source)) errors.source = 'Choose a source.';

    let amount = null;
    if (!isBlank(d.amountText) && String(d.amountText).trim() !== '') {
        amount = rupeesToPaise(d.amountText);
        if (amount === null) errors.amount_agreed_paise = 'Enter the amount in rupees, for example 15,000 or 15,000.50.';
    }

    let validTo = null;
    if (!isBlank(d.validTo)) {
        validTo = endOfDayIso(d.validTo, timeZone);
        if (validTo === null) errors.valid_to = 'Enter a date.';
    }

    const reason = cleanReason(d.reason);
    if (!reason.ok) errors.reason = reason.error;

    if (Object.keys(errors).length) return { ok: false, errors, body: null };

    if (!isPlainObject(current)) {
        body.plan_code = d.planCode;
        body.status = d.status;
        body.source = d.source;
        if (amount !== null) body.amount_agreed_paise = amount;
        if (validTo !== null) body.valid_to = validTo;
    } else {
        if (d.planCode !== current.plan_code) body.plan_code = d.planCode;
        if (d.status !== current.status) body.status = d.status;
        if (d.source !== current.source) body.source = d.source;
        const was = isBlank(current.amount_agreed_paise) ? null : Number(current.amount_agreed_paise);
        if (amount !== was) body.amount_agreed_paise = amount;
        // the same calendar day as before is no change (it would only move the time of day)
        if (toDateInput(current.valid_to, timeZone) !== (d.validTo || '')) body.valid_to = validTo;
        if (!Object.keys(body).length) return { ok: false, errors: { form: 'Nothing has changed.' }, body: null };
    }
    body.reason = reason.value;
    return { ok: true, errors: {}, body };
}

/**
 * The body of PUT /api/licence/plans/:plan_code from the plan form: only the fields that differ from `plan`.
 * `draft`: { name, description, sellable, active, listPriceText, minPriceText, termText, graceText } (texts for the numbers).
 */
function planPatch(plan, draft) {
    const d = draft || {};
    const p = plan || {};
    const errors = {};
    const patch = {};

    const name = typeof d.name === 'string' ? d.name.trim() : '';
    if (!name) errors.name = 'Enter a name.';
    else if (name.length > 120) errors.name = 'Keep the name to 120 characters or fewer.';
    else if (name !== p.name) patch.name = name;

    const description = typeof d.description === 'string' ? d.description.trim() : '';
    if (description.length > 2000) errors.description = 'Keep the description to 2000 characters or fewer.';
    else if (description !== (p.description || '')) patch.description = description === '' ? null : description;

    if (!!d.sellable !== !!p.sellable) patch.sellable = !!d.sellable;
    if (!!d.active !== !!p.active) patch.active = !!d.active;

    const price = (text, key, label) => {
        if (isBlank(text) || String(text).trim() === '') return null;
        const paise = rupeesToPaise(text);
        if (paise === null) errors[key] = `Enter the ${label} in rupees, for example 15,000.`;
        return paise;
    };
    const list = price(d.listPriceText, 'list_price_paise', 'list price');
    const min = price(d.minPriceText, 'min_price_paise', 'minimum price');
    if (!errors.list_price_paise && list !== (isBlank(p.list_price_paise) ? null : Number(p.list_price_paise))) patch.list_price_paise = list;
    if (!errors.min_price_paise && min !== (isBlank(p.min_price_paise) ? null : Number(p.min_price_paise))) patch.min_price_paise = min;
    if (!errors.list_price_paise && !errors.min_price_paise && list !== null && min !== null && min > list) errors.min_price_paise = 'The minimum cannot be above the list price.';

    const whole = (text, key, label, lo, hi) => {
        if (isBlank(text) || String(text).trim() === '') return null;
        const t = String(text).trim();
        const n = /^\d{1,6}$/.test(t) ? Number(t) : NaN;
        if (!Number.isInteger(n) || n < lo || n > hi) {
            errors[key] = `Enter a whole number from ${lo} to ${hi}, or leave it empty.`;
            return undefined;
        }
        return n;
    };
    const term = whole(d.termText, 'term_months', 'term', 1, 600);
    const grace = whole(d.graceText, 'grace_days', 'grace days', 0, 3650);
    if (term !== undefined && term !== (isBlank(p.term_months) ? null : Number(p.term_months))) patch.term_months = term;
    if (grace !== undefined && grace !== (isBlank(p.grace_days) ? null : Number(p.grace_days))) patch.grace_days = grace;

    if (p.plan_code === 'LEGACY_HEPI' && patch.sellable === true) errors.sellable = 'The legacy plan is not sold.';

    if (Object.keys(errors).length) return { ok: false, errors, patch: null };
    if (!Object.keys(patch).length) return { ok: false, errors: { form: 'Nothing has changed.' }, patch: null };
    return { ok: true, errors: {}, patch };
}

// ------------------------------------------------------------------------------------------------
// Settings
// ------------------------------------------------------------------------------------------------

/**
 * How each setting is edited. Known settings get a simple control; any other setting (a new one the server has added) is
 * shown by what its value looks like.
 */
const SETTING_META = {
    grace_days: { kind: 'number', title: 'Grace days', min: 0, max: 365 },
    claim_token_hours: { kind: 'number', title: 'TV claim token lifetime (hours)', min: 1, max: 168 },
    payment_link_hours: { kind: 'number', title: 'Payment link lifetime (hours)', min: 1, max: 720 },
    kyc_require_signatory_otp: { kind: 'boolean', title: 'KYC needs the signatory OTP' },
    activation_requires_kyc: { kind: 'boolean', title: 'Activation needs approved KYC' },
    enforce_all: { kind: 'boolean', title: 'Enforce all', warn: 'Every organisation with a licence is held to its plan, whatever its own switch says.' },
    licence_assign_requires_kyc: { kind: 'enum', title: 'KYC needed to assign a licence', options: [['submitted', 'KYC submitted'], ['none', 'No KYC needed']] },
    tv_registration_requires: { kind: 'enum', title: 'Needed before a TV is registered', options: [['active_licence', 'An active licence'], ['kyc_approved', 'Approved KYC'], ['none', 'Nothing']] },
    unlicensed_policy: { kind: 'enum', title: 'Organisation without a licence', options: [['allow', 'Allow: never held back'], ['core_only', 'Core only (while Enforce all is on)']] },
    requests_device_binding: { kind: 'enum', title: 'TV requests must match the device', options: [['on', 'On'], ['off', 'Off']] },
    check: { kind: 'enum', title: 'API licence check', options: [['on', 'On'], ['off', 'Off (emergency switch)']], warn: 'Off stops the licence check on every API call.' },
    status_capabilities: { kind: 'json', title: 'What each status includes', expect: 'object' },
    notices: { kind: 'json', title: 'Notices (English)', expect: 'object' },
    role_permissions: { kind: 'json', title: 'Who may do what', expect: 'object' },
    kyc_required_documents: { kind: 'json', title: 'KYC documents required', expect: 'array' },
    return_url_schemes: { kind: 'json', title: 'Allowed return-address schemes', expect: 'array' }
};

/** { kind: 'boolean'|'number'|'enum'|'json'|'text', title, ... } for a setting. */
function settingMeta(key, value) {
    if (SETTING_META[key]) return SETTING_META[key];
    if (typeof value === 'boolean') return { kind: 'boolean', title: humanize(key) };
    if (typeof value === 'number') return { kind: 'number', title: humanize(key) };
    if (typeof value === 'string') return { kind: 'text', title: humanize(key) };
    return { kind: 'json', title: humanize(key), expect: Array.isArray(value) ? 'array' : 'object' };
}

/** Text from a JSON box -> { ok, value } or { ok: false, error }. `expect` is 'object' or 'array' (or nothing). */
function parseJsonSetting(text, expect) {
    let value;
    try {
        value = JSON.parse(text);
    } catch (e) {
        return { ok: false, error: 'This is not valid JSON. Check the commas, quotes and brackets.' };
    }
    if (expect === 'object' && !isPlainObject(value)) return { ok: false, error: 'This must be a JSON object, like {"a": 1}.' };
    if (expect === 'array' && !Array.isArray(value)) return { ok: false, error: 'This must be a JSON list, like ["a", "b"].' };
    return { ok: true, value };
}

/** The settings whose edited value differs from the saved one: { key: value }. */
function changedSettings(saved, edited) {
    const out = {};
    for (const key of Object.keys(edited || {})) {
        if (!deepEqual((saved || {})[key], edited[key])) out[key] = edited[key];
    }
    return out;
}

module.exports = {
    LICENCE_KEY, SWITCHED_OFF_KEY, NOT_IN_PLAN_ROUTE, NEVER_REDIRECTED, STATUSES, SOURCES, REASON_MAX, NOTE_MAX, SETTING_META,
    humanize, escapeHtml, deepEqual, isPlainObject,
    browserStorage, readLicence, storeLicence, readSwitchedOff, storeSwitchedOff, isNotInPlan, isSwitchedOff, guardTarget,
    licenceError, licenceErrorOutcome, normaliseError, fieldErrorLines,
    formatPaise, rupeesToPaise, paiseToRupeesInput, formatDate, formatDateTime, toDateInput, endOfDayIso, fileSizeLabel,
    statusLabel, statusTone, capabilityTitle, planName, proposeLabel, confidenceLabel, sourceLabel, enforcementLabel, blockerLabel, blockersText,
    kycStatusLabel, kycStatusTone, paymentStatusLabel, paymentStatusTone, paymentMethodLabel, docTypeLabel, checkLabel, checkTone, checkModeLabel,
    observationKindLabel, orgTypeLabel, eventLabel, actorLabel, describeChange, overridesText,
    staffFlags, availableActions, cleanReason, suggestSource, planChoices,
    licenceChangeBody, planPatch,
    settingMeta, parseJsonSetting, changedSettings
};
